
import React, { useRef, useState, useEffect } from "react";
import { formatTime } from "../../services/youtubeService";

type Props = {
  start: number;
  end: number;
  maxDuration: number;
  onChange: (s: number, e: number) => void;
  onSeek: (time: number) => void;
};

export const YouTubeSegmentSelector: React.FC<Props> = ({ start, end, maxDuration, onChange, onSeek }) => {
  const railRef = useRef<HTMLDivElement>(null);
  const [activeHandle, setActiveHandle] = useState<'start' | 'end' | null>(null);

  // Conversion Coordonnées -> Temps
  const getTimeFromX = (clientX: number) => {
    if (!railRef.current) return 0;
    const rect = railRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const time = (x / rect.width) * maxDuration;
    return Number(time.toFixed(2));
  };

  const handleMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!activeHandle) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const time = getTimeFromX(clientX);

    if (activeHandle === 'start') {
      const nextStart = Math.min(time, end - 0.5);
      onChange(nextStart, end);
      onSeek(nextStart);
    } else {
      const nextEnd = Math.max(time, start + 0.5);
      onChange(start, nextEnd);
      onSeek(nextEnd);
    }
  };

  const handleMouseUp = () => setActiveHandle(null);

  useEffect(() => {
    if (activeHandle) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [activeHandle, start, end]);

  const startPct = (start / maxDuration) * 100;
  const endPct = (end / maxDuration) * 100;
  const duration = end - start;

  return (
    <div className="bg-black/60 p-6 md:p-8 rounded-[48px] border border-white/10 space-y-8 shadow-2xl relative overflow-hidden h-full flex flex-col justify-center">
      <div className="absolute top-0 right-0 p-8 opacity-5 text-6xl font-black italic pointer-events-none uppercase">Sampler</div>
      
      <div className="flex justify-between items-end mb-4">
        <div className="space-y-1">
          <h4 className="text-sm font-black text-white uppercase italic tracking-widest">Interactive Loop Seeker</h4>
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Glissez les curseurs pour isoler le riff</p>
        </div>
        <div className="px-5 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-2xl flex flex-col items-end shadow-[0_0_20px_rgba(6,182,212,0.1)]">
           <span className="text-[8px] font-black text-cyan-400 uppercase tracking-widest text-right">Durée isolée</span>
           <span className="text-2xl font-black text-white italic">{duration.toFixed(1)}s</span>
        </div>
      </div>

      {/* INTERACTIVE RAIL */}
      <div className="relative py-10">
        <div 
          ref={railRef}
          className="h-16 w-full bg-white/5 rounded-3xl border border-white/10 relative overflow-visible cursor-crosshair touch-none"
        >
          {/* Active Loop Highlight */}
          <div 
            className="absolute top-0 bottom-0 bg-gradient-to-r from-cyan-500/30 via-cyan-400/10 to-smart-gold/30 border-x-2 border-white/20 shadow-[inset_0_0_30px_rgba(255,255,255,0.05)]"
            style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
          />

          {/* Start Handle (A) */}
          <div 
            onMouseDown={() => setActiveHandle('start')}
            onTouchStart={() => setActiveHandle('start')}
            className="absolute top-1/2 -translate-y-1/2 -ml-6 w-12 h-20 flex items-center justify-center cursor-ew-resize z-30 group"
            style={{ left: `${startPct}%` }}
          >
            <div className="w-1.5 h-12 bg-cyan-400 rounded-full shadow-[0_0_15px_#22d3ee] group-hover:scale-y-110 transition-transform" />
            <div className="absolute -top-8 bg-cyan-400 text-black text-[10px] font-black px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">A</div>
          </div>

          {/* End Handle (B) */}
          <div 
            onMouseDown={() => setActiveHandle('end')}
            onTouchStart={() => setActiveHandle('end')}
            className="absolute top-1/2 -translate-y-1/2 -ml-6 w-12 h-20 flex items-center justify-center cursor-ew-resize z-30 group"
            style={{ left: `${endPct}%` }}
          >
            <div className="w-1.5 h-12 bg-smart-gold rounded-full shadow-[0_0_15px_#DA9000] group-hover:scale-y-110 transition-transform" />
            <div className="absolute -top-8 bg-smart-gold text-black text-[10px] font-black px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">B</div>
          </div>

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-[7px] font-black text-white/10 uppercase tracking-[1.5em] italic">Precision Timeline Mapper</span>
          </div>
        </div>
      </div>

      {/* MICRO ADJUSTMENTS */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 p-4 rounded-3xl border border-white/5 flex flex-col gap-1">
          <span className="text-[8px] font-black text-cyan-400 uppercase tracking-widest">Start Time</span>
          <span className="text-xl font-mono font-black text-white">
            {formatTime(start)}<span className="text-xs text-slate-500">.{(start % 1).toFixed(2).split('.')[1]}</span>
          </span>
        </div>
        <div className="bg-white/5 p-4 rounded-3xl border border-white/5 flex flex-col gap-1">
          <span className="text-[8px] font-black text-smart-gold uppercase tracking-widest">End Time</span>
          <span className="text-xl font-mono font-black text-white">
            {formatTime(end)}<span className="text-xs text-slate-500">.{(end % 1).toFixed(2).split('.')[1]}</span>
          </span>
        </div>
      </div>

      <div className="flex gap-2 bg-black/40 p-2 rounded-2xl border border-white/5">
        {[4, 8, 16, 32].map(s => (
          <button 
            key={s} 
            onClick={() => onChange(start, Math.min(maxDuration, start + s))}
            className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${duration === s ? 'bg-white text-black' : 'hover:bg-white/5 text-slate-500'}`}
          >
            {s}s Loop
          </button>
        ))}
      </div>
    </div>
  );
};
